const PAYMENT_METHOD_TYPES = [
    'CREDIT_CARD',
    'ACH',
    'ON_PLATFORM_WIRE_TRANSFER',
    'WIRE_TRANSFER',
    'PAYPAL_ACCOUNT',
    'APPLE_PAY',
    'KLARNA',
] as const;
export type PaymentMethodType = (typeof PAYMENT_METHOD_TYPES)[number];

const PAYMENT_PROCESSORS = ['STRIPE', 'BRAINTREE', 'OFF_PLATFORM', 'KLARNA'] as const;
export type PaymentProcessor = (typeof PAYMENT_PROCESSORS)[number];

export type PaymentMethod = {
    paymentMethodType: PaymentMethodType;
    paymentProcessor: PaymentProcessor;
    paymentMethodToken?: string;
    paymentMethodNonce?: string;
    authorizationCode?: string;
    hashedCardNumber?: string;
    // used for verification, ignored in favor of `billingAddress` prop otherwise
    billingAddress?: Address | null;
};

export type PaymentMethodOption = {
    paymentMethodType: PaymentMethodType;
    paymentProcessor: PaymentProcessor;
    processorPaymentMethodName?: string;
};

export type PaymentMethodOptionInput = {
    paymentMethodType: string | null | undefined;
    paymentProcessor: string | null | undefined;
    processorPaymentMethodName?: string | null;
};

export type Address = {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    line1?: string | null;
    line2?: string | null;
    country?: string | null;
    zipCode?: string | null;
    company?: string | null;
    state?: string | null;
    city?: string | null;
};

export type ChargeCurrency = 'USD' | 'EUR' | 'GBP';

const WALLET_PAYMENT_METHODS: readonly PaymentMethodType[] = ['CREDIT_CARD', 'ACH'] as const;

export function isWalletPaymentMethod(paymentMethod: PaymentMethod | PaymentMethodOption): boolean {
    return WALLET_PAYMENT_METHODS.includes(paymentMethod.paymentMethodType);
}

export function isStripeElementPaymentMethod(
    paymentMethod: PaymentMethod | PaymentMethodOption | null
): boolean {
    if (!paymentMethod) {
        return false;
    }

    return (
        paymentMethod.paymentProcessor === 'STRIPE' &&
        paymentMethod.paymentMethodType !== 'ON_PLATFORM_WIRE_TRANSFER'
    );
}

export function isPaymentMethodType(
    paymentMethodType: string | null | undefined
): paymentMethodType is PaymentMethodType {
    return (
        !!paymentMethodType &&
        (PAYMENT_METHOD_TYPES as ReadonlyArray<string>).includes(paymentMethodType)
    );
}

export function isPaymentProcessor(
    paymentProcessor: string | null | undefined
): paymentProcessor is PaymentProcessor {
    return (
        !!paymentProcessor &&
        (PAYMENT_PROCESSORS as ReadonlyArray<string>).includes(paymentProcessor)
    );
}

export function parsePaymentMethodOptionInput(
    paymentMethodOptionInput: PaymentMethodOptionInput
): PaymentMethodOption | null {
    const { paymentMethodType, paymentProcessor, processorPaymentMethodName } =
        paymentMethodOptionInput;

    if (!isPaymentMethodType(paymentMethodType)) {
        return null;
    }

    if (!isPaymentProcessor(paymentProcessor)) {
        return null;
    }

    return {
        paymentMethodType,
        paymentProcessor,
        processorPaymentMethodName: processorPaymentMethodName || undefined,
    };
}
